import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    HostBinding,
    OnDestroy,
    OnInit,
    ViewChild,
    ViewEncapsulation,
    NgZone
} from '@angular/core';
import * as objectPath from 'object-path';
import {DomSanitizer, Meta} from '@angular/platform-browser';
import {NavigationEnd, Router} from '@angular/router';
import {filter} from 'rxjs/operators';
import {CSDataLayerService} from '@service/services/server/csdatalayer.service';
import {locale as enLang} from './config/i18n/en';
import {LayoutConfigService} from '@core/util-services/layout-config.service';
import {ClassInitService} from '@core/util-services/class-init.service';
import {TranslationService} from '@core/util-services/translation.service';
import {PageConfigService} from '@core/util-services/page-config.service';
import {SplashScreenService} from '@core/util-services/splash-screen.service';
import {environment} from '@env/environment';
import {AppConfigService} from '@service/config/app-config.service';
import {ScriptLoaderService} from '@core/util-services/script-loader.service';
import {LogRocketService} from '@core/util-services/logrocket.service';
import {KeyIdentifierModel} from '@service/models';
import {SharedkeyDataService} from '@service/services/common-services/sharedkey-data.service';
import {MatDialog} from '@angular/material/dialog';

@Component({
    selector: 'body[m-root]',
    templateUrl: './app.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None
})
export class AppComponent implements AfterViewInit, OnInit, OnDestroy {
    title = 'CreditSnap';
    private timeout: any;
    private resizeTimeout: any;
    private readonly timeoutDuration = 10 * 60 * 1000; // 10 minutes
    keyIdentifier: KeyIdentifierModel;
    @HostBinding('style') style: any;
    @HostBinding('class') classes: any = '';

    @ViewChild('splashScreen', {read: ElementRef, static: true})
    splashScreen: ElementRef;
    splashScreenImage: string;
    favIcon: HTMLLinkElement = document.querySelector('#favIcon');
    appConfig: any = {};

    constructor(
        private layoutConfigService: LayoutConfigService,
        private classInitService: ClassInitService,
        private sanitizer: DomSanitizer,
        private translationService: TranslationService,
        private router: Router,
        private pageConfigService: PageConfigService,
        private splashScreenService: SplashScreenService,
        private scriptLoaderService: ScriptLoaderService,
        private logRocketService: LogRocketService,
        private sharedKeyDataService: SharedkeyDataService,
        private ngZone: NgZone,
        private dialog: MatDialog,
        private meta: Meta,
        private csDataLayerService: CSDataLayerService,
    ) {
        this.appConfig = AppConfigService.config;
        this.sharedKeyDataService.keyIdentifier.subscribe(data => {
            this.keyIdentifier = data;
        });
        // subscribe to class update event
        this.classInitService.onClassesUpdated$.subscribe(classes => {
            // get body class array, join as string classes and pass to host binding class
            setTimeout(() => this.classes = classes.body.join(' '));
        });

        this.layoutConfigService.onLayoutConfigUpdated$.subscribe(model => {
            this.classInitService.setConfig(model);

            this.style = '';
            if (objectPath.get(model.config, 'self.layout') === 'boxed') {
                const backgroundImage = objectPath.get(model.config, 'self.background');
                if (backgroundImage) {
                    this.style = this.sanitizer.bypassSecurityTrustStyle('background-image: url(' +
                        objectPath.get(model.config, 'self.background') + ')');
                }
            }
            // this.splashScreenImage = objectPath.get(model.config, 'loader.image') + environment.institutionId + '.png';
            this.splashScreenImage = environment.s3Url + 'images/' + environment.institutionId + '/' + 'loading.gif';
        });

        // register translations
        this.translationService.loadTranslations(enLang);

        // override config by router change from pages config
        this.router.events
            .pipe(filter(event => event instanceof NavigationEnd))
            .subscribe(event => {
                this.layoutConfigService.setModel({page: objectPath.get(this.pageConfigService.getCurrentPageConfig(), 'config')}, true);
            });
    }

    // Global GA Clicks
    private readonly trackClick = (event: MouseEvent) => this.csDataLayerService.trackClick(event);

    ngOnInit(): void {
        document.addEventListener('click', this.trackClick, true);
        if (!environment.production) {
            this.meta.addTag({name: 'robots', content: 'noindex, nofollow'});
        }

        this.scriptLoaderService.load({
            name: 'HeadScript',
            src: 'https://creditsnap-public.s3.amazonaws.com/images/' + environment.institutionId + '/scripts/head.js',
            loaded: false
        }).subscribe();


        this.scriptLoaderService.load({
            name: 'BodyScript',
            src: 'https://creditsnap-public.s3.amazonaws.com/images/' + environment.institutionId + '/scripts/body.js',
            loaded: false
        }, 'body').subscribe();

        this.favIcon.href = 'https://creditsnap-public.s3.amazonaws.com/images/' + environment.institutionId + '/fav.png';
        if (this.appConfig.themeColors) {
            this.changeTheme(this.appConfig.themeColors?.primaryColor,
            );
        }
        this.applyHeaderTheme();
        this.initCookieConsent();
        this.logRocketService.init();
        this.resetTimer();
        window.addEventListener('mousemove', this.resetTimer.bind(this));
        window.addEventListener('keydown', this.resetTimer.bind(this));
        window.addEventListener('mousedown', this.resetTimer.bind(this));
        window.addEventListener('touchstart', this.resetTimer.bind(this));
        window.addEventListener('resize', this.onWindowResize);
    }
    private onWindowResize = () => {
        document.body.classList.add('disable-transitions');
        clearTimeout(this.resizeTimeout);
        this.resizeTimeout = setTimeout(() => {
            document.body.classList.remove('disable-transitions');
        }, 150);
    }

    changeTheme(primaryColor: string) {
        if (primaryColor) {
            document.documentElement.style.setProperty('--primary-color', primaryColor);
        }
    }

    applyHeaderTheme(): void {
        const headerConfig = this.appConfig?.headerConfig;
        if (!headerConfig) { return; }
        const root = document.documentElement.style;
        if (headerConfig.headerTop?.BGColor) {
            root.setProperty('--header-top-bg', headerConfig.headerTop.BGColor);
        }
        if (headerConfig.headerBottom?.BGColor) {
            root.setProperty('--header-bottom-bg', headerConfig.headerBottom.BGColor);
        }
        if (headerConfig.headerBottom?.FontColor) {
            root.setProperty('--header-bottom-font-color', headerConfig.headerBottom.FontColor);
        }
    }

    initCookieConsent(): void {
        const cc = window as any;
        if (!cc.cookieconsent) { return; }
        cc.cookieconsent.initialise({
            cookie: { domain: window.location.hostname },
            position: 'bottom',
            theme: 'classic',
            type: 'info',
            content: {
                message: 'We are using cookies to ensure you get the best experience on our website.',
                dismiss: 'Got it!',
                deny: 'Decline',
                link: 'Learn more',
                href: 'https://creditsnap-public.s3.amazonaws.com/html/ABC/cookie+policy.html',
                policy: 'Cookie Policy',
                header: 'Cookies used on the website!',
                allow: 'Allow cookies'
            }
        });
        this.patchCookieConsentA11y();
    }

    private patchCookieConsentA11y(): void {
         const banner = document.querySelector('.cc-window.cc-banner') as HTMLElement;
        if (banner) { banner.setAttribute('aria-modal', 'true'); }
        const ccWindow = document.querySelector('.cc-window') as HTMLElement | null;
        if (!ccWindow) {
            // Banner not in DOM yet — retry on next animation frame
            requestAnimationFrame(() => this.patchCookieConsentA11y());
            return;
        }

        // ARIA: non-modal notification strip, not a dialog
        ccWindow.setAttribute('role', 'status');
        ccWindow.setAttribute('aria-label', 'Cookie notice');
        ccWindow.setAttribute('aria-live', 'polite');

        // Safety: remove any residual inline colour overrides so the CSS rules
        // in styles.scss (white bg + primary-color text) always win.
        const dismissBtn = ccWindow.querySelector('.cc-btn.cc-dismiss') as HTMLElement | null;
        if (dismissBtn) {
            dismissBtn.style.removeProperty('background-color');
            dismissBtn.style.removeProperty('color');
            // <a role="button"> doesn't fire click on Space natively — add it (WCAG 2.1.1)
            dismissBtn.addEventListener('keydown', (e: KeyboardEvent) => {
                if (e.key === ' ' || e.code === 'Space') {
                    e.preventDefault();
                    dismissBtn.click();
                }
            });
        }

        // "Learn more" is a native <a href> — keep link semantics, add accessible label.
        const learnMoreLink = ccWindow.querySelector('.cc-link') as HTMLElement | null;
        if (learnMoreLink) {
            learnMoreLink.removeAttribute('role');
            learnMoreLink.setAttribute('aria-label', 'learn more about cookies');
        }
    }

    ngAfterViewInit(): void {
        if (this.splashScreen) {
            this.splashScreenService.init(this.splashScreen.nativeElement);
        }

        setTimeout(() => this.hideDecorativeSvgs(), 1500);
    }

    private hideDecorativeSvgs(): void {
        document.querySelectorAll('svg').forEach((svg: Element) => {
            const hasName = svg.hasAttribute('aria-label') ||
                svg.hasAttribute('aria-labelledby') ||
                svg.querySelector(':scope > title');
            const alreadyHidden = svg.getAttribute('aria-hidden') === 'true' ||
                !!svg.closest('[aria-hidden="true"]');
            const isImg = svg.getAttribute('role') === 'img';
            if (!hasName && !alreadyHidden && !isImg) {
                svg.setAttribute('aria-hidden', 'true');
            }
        });
    }

    private resetTimer() {
        if (this.timeout) {
            clearTimeout(this.timeout);
        }
        if (this.keyIdentifier && this.keyIdentifier.applicationId) {
            this.timeout = setTimeout(() => {
                this.dialog.closeAll();
                this.ngZone.run(() => this.router.navigate(['/myapp']));
            }, this.timeoutDuration);
        }
    }

    ngOnDestroy() {
        document.removeEventListener('click', this.trackClick, true);
        clearTimeout(this.timeout);
        clearTimeout(this.resizeTimeout);
        window.removeEventListener('mousemove', this.resetTimer.bind(this));
        window.removeEventListener('keydown', this.resetTimer.bind(this));
        window.removeEventListener('mousedown', this.resetTimer.bind(this));
        window.removeEventListener('touchstart', this.resetTimer.bind(this));
        window.removeEventListener('resize', this.onWindowResize);
    }
}
